import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useAuth } from "../contexts/AuthContext";
import SmartBackButton from "../components/ui/SmartBackButton";
import toast from "react-hot-toast";
import { ENTITY_NAMES } from "../constants/entities";
import PaymentForm from "../components/payments/PaymentForm";

interface Payment {
  id: string;
  created_at: string;
  student_id: string;
  run_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  notes: string | null;
  stripe_payment_intent_id: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  currency: string | null;
  status: string;
}

interface UpdatePayment {
  student_id?: string;
  run_id?: string;
  amount?: number;
  payment_date?: string;
  payment_method?: string;
  notes?: string | null;
}

const EditPayment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [payment, setPayment] = useState<Payment | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPayment, setIsLoadingPayment] = useState(true);
  const { userRole } = useAuth();
  const navigate = useNavigate();

  const isAdmin = userRole === "admin";

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        setIsLoadingPayment(true);

        if (!id) return;

        const { data, error } = await supabase
          .from("payments")
          .select("*")
          .eq("id", id)
          .single();

        if (error) throw error;

        setPayment(data);
      } catch (error) {
        console.error("Error fetching payment:", error);
        toast.error("Failed to load payment details");
        navigate("/payments");
      } finally {
        setIsLoadingPayment(false);
      }
    };

    fetchPayment();
  }, [id, navigate]);

  const handleSubmit = async (data: UpdatePayment) => {
    try {
      setIsLoading(true);

      if (!id) return;

      const { error } = await supabase
        .from("payments")
        .update(data)
        .eq("id", id);

      if (error) throw error;

      toast.success(`${ENTITY_NAMES.PAYMENT} updated successfully`);
      navigate(`/payments/${id}`);
    } catch (error) {
      console.error("Error updating payment:", error);
      toast.error(`Failed to update ${ENTITY_NAMES.PAYMENT.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Access Denied
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Only administrators can edit payments.
        </p>
      </div>
    );
  }

  if (isLoadingPayment) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center mb-6">
          <div className="w-32 h-8 bg-gray-200 rounded"></div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="w-48 h-8 bg-gray-200 rounded mb-4"></div>
          <div className="w-full h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="text-center py-12">
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          Payment not found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          The payment you're looking for doesn't exist.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center mb-6">
        <SmartBackButton
          fallbackPath={`/payments/${id}`}
          className="text-link hover:text-hover flex items-center"
        >
          Back to Payment
        </SmartBackButton>
      </div>

      {/* Form */}
      <div className="bg-white shadow-sm rounded-lg border border-neutral-200 overflow-hidden">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h1 className="">Edit {ENTITY_NAMES.PAYMENT}</h1>
          </div>

          <PaymentForm
            defaultValues={payment}
            onSubmit={handleSubmit}
            isLoading={isLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default EditPayment;
